import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ChartLine,
  ChevronLeft,
  ChevronRight,
  Info,
  Table2,
} from "lucide-react";
import { isAxiosError } from "axios";
import dayjs from "dayjs";
import { useAnalystEstimates } from "../hooks/useAnalystEstimates";
import { useCompanyIncomeStatement } from "../hooks/useCompanyIncomeStatement";
import { useStockCurrency } from "../../../../contexts/StockCurrencyContext";
import { getChartColors } from "../constants/chartColors";
import { formatNumber } from "../utils/formatNumber";
import { roundedPanelSx, panelTitleSx } from "../../overviewPanel";
import {
  AnalystEstimatesChart,
  ESTIMATE_LEVELS,
} from "./AnalystEstimatesChart";
import {
  EstimateMetric,
  EstimatePeriod,
  getEstimateRanges,
  getActualResults,
  getAvailableForecastHorizons,
  getForecastWindow,
} from "./analystEstimatesData";
import ChartCardWrapper from "./shared/ChartCardWrapper";
import {
  MAX_ANNUAL_LIMIT,
  MAX_QUARTERLY_LIMIT,
} from "./shared/financialPeriodConfig";

interface AnalystEstimatesCardProps {
  ticker: string;
}

export const AnalystEstimatesCard: React.FC<AnalystEstimatesCardProps> = ({
  ticker,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currencySymbol, formatDisplayPrice } = useStockCurrency();
  const [period, setPeriod] = useState<EstimatePeriod>("annual");
  const [metric, setMetric] = useState<EstimateMetric>("revenue");
  const [view, setView] = useState<"chart" | "table">("chart");
  const [preferredHorizon, setPreferredHorizon] = useState(5);
  const [quarterHorizon, setQuarterHorizon] = useState<{
    ticker: string;
    metric: EstimateMetric;
    value: number;
  } | null>(null);
  const changePeriod = (nextPeriod: EstimatePeriod) => {
    if (nextPeriod !== period) setQuarterHorizon(null);
    setPeriod(nextPeriod);
  };
  const query = useAnalystEstimates(ticker, {
    period,
    limit: period === "annual" ? 15 : 24,
  });
  const income = useCompanyIncomeStatement(
    ticker,
    period === "annual" ? MAX_ANNUAL_LIMIT : MAX_QUARTERLY_LIMIT,
    period,
  );
  const actuals = useMemo(
    () => getActualResults(income.data ?? [], metric, period),
    [income.data, metric, period],
  );
  const latestActual = actuals[actuals.length - 1];
  const cutoff = (
    latestActual
      ? dayjs(latestActual.date).endOf(period === "annual" ? "year" : "quarter")
      : dayjs()
          .startOf(period === "annual" ? "year" : "quarter")
          .subtract(1, "day")
  ).format("YYYY-MM-DD");
  const allRanges = useMemo(
    () => getEstimateRanges(query.data ?? [], metric, period),
    [query.data, metric, period],
  );
  const availableHorizons = useMemo(
    () => getAvailableForecastHorizons(allRanges, cutoff, period),
    [allRanges, cutoff, period],
  );
  const preferredQuarterHorizon =
    quarterHorizon?.ticker === ticker && quarterHorizon.metric === metric
      ? quarterHorizon.value
      : Infinity;
  const preferredWindow =
    period === "annual" ? preferredHorizon : preferredQuarterHorizon;
  const horizon =
    availableHorizons.filter((value) => value <= preferredWindow).pop() ??
    availableHorizons[0];
  const horizonUnit = period === "annual" ? "year" : "quarter";
  const horizonLabel = horizon
    ? `${horizon} ${horizonUnit}${horizon === 1 ? "" : "s"}`
    : "Horizon";
  const ranges = useMemo(
    () => getForecastWindow(allRanges, cutoff, horizon ?? 1, period),
    [allRanges, cutoff, horizon, period],
  );
  const metricLabel = metric === "revenue" ? "Revenue" : "EPS";
  const periodLabel = period === "annual" ? "Annual" : "Quarterly";
  const periodColumn = period === "annual" ? "Year" : "Quarter";
  const viewAction =
    view === "chart" ? "Show estimates table" : "Show estimates chart";
  const toggleView = () =>
    setView((current) => (current === "chart" ? "table" : "chart"));
  const chartColors = getChartColors(isDark);
  const colors = {
    actual: chartColors.positive,
    low: chartColors.negative,
    average: chartColors.warning,
    high: chartColors.positive,
  };
  const formatValue = (value: number | null) => {
    if (value === null) return "—";
    if (metric === "eps") return formatDisplayPrice(value);
    return `${currencySymbol}${formatNumber(value, 2)}`;
  };
  const notFound =
    isAxiosError(query.error) && query.error.response?.status === 404;
  const incomeNotFound =
    isAxiosError(income.error) && income.error.response?.status === 404;
  const queryFailed = Boolean(query.error && !notFound);
  const incomeFailed = Boolean(income.error && !incomeNotFound);
  const hasError = queryFailed || incomeFailed;
  const isLoading = query.isLoading || income.isLoading;
  const hasData = availableHorizons.length > 0;
  const canShowData = !isLoading && !hasError && hasData;
  const showEmpty = !isLoading && !hasError && !hasData;
  const horizonIndex = availableHorizons.indexOf(horizon);
  const stepHorizon = (direction: -1 | 1) => {
    if (!canShowData) return;
    const nextHorizon = availableHorizons[horizonIndex + direction];
    if (nextHorizon === undefined) return;
    if (period === "annual") setPreferredHorizon(nextHorizon);
    else setQuarterHorizon({ ticker, metric, value: nextHorizon });
  };
  const controlSx = {
    height: { xs: 44, sm: 34 },
    fontSize: "0.75rem",
    fontWeight: 500,
    borderRadius: 1,
    bgcolor: "background.default",
    "& .MuiSelect-select": { py: 0.75, pl: 1.25 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
  };

  return (
    <Box
      component="section"
      aria-label="Analyst estimates"
      sx={{
        pb: 2,
        "& > .MuiCard-root": {
          ...roundedPanelSx(isDark),
          p: 0,
        },
        "& > .MuiCard-root > .MuiCardContent-root": { p: 2 },
      }}
    >
      <ChartCardWrapper renderCard>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr) auto",
              md: "minmax(0, 1fr) auto auto",
            },
            gridTemplateAreas: {
              xs: '"title view" "controls controls"',
              md: '"title controls view"',
            },
            alignItems: "center",
            columnGap: 1,
            rowGap: 1,
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              gridArea: "title",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              component="h2"
              sx={{
                ...panelTitleSx,
                color: "text.primary",
                borderLeft: 2,
                borderColor: "primary.main",
                pl: 1,
              }}
            >
              Analyst estimates
            </Typography>
            <Tooltip title="Reported results lead into dotted low, average and high analyst forecasts. Hover or tap a period for details.">
              <IconButton
                size="small"
                aria-label="About analyst estimate ranges"
                sx={{ color: "text.secondary" }}
              >
                <Info size={15} />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              gridArea: "controls",
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr) 160px",
                sm: "104px 110px 128px",
              },
              alignItems: "center",
              gap: 1,
              justifySelf: "end",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {isMobile && (
              <Select
                value={`${metric}:${period}`}
                onChange={(event) => {
                  const [nextMetric, nextPeriod] = event.target.value.split(
                    ":",
                  ) as [EstimateMetric, EstimatePeriod];
                  setMetric(nextMetric);
                  changePeriod(nextPeriod);
                }}
                inputProps={{ "aria-label": "Estimate metric and period" }}
                renderValue={() => (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.125,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {metricLabel}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "0.625rem",
                        color: "text.secondary",
                        lineHeight: 1.3,
                      }}
                    >
                      {periodLabel}
                    </Typography>
                  </Box>
                )}
                sx={{ ...controlSx, minWidth: 0 }}
                MenuProps={{ PaperProps: { sx: { minWidth: 220 } } }}
              >
                {[
                  {
                    value: "revenue:annual",
                    metric: "Revenue",
                    period: "Annual",
                  },
                  {
                    value: "revenue:quarter",
                    metric: "Revenue",
                    period: "Quarterly",
                  },
                  { value: "eps:annual", metric: "EPS", period: "Annual" },
                  { value: "eps:quarter", metric: "EPS", period: "Quarterly" },
                ].map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    aria-label={`${option.metric} · ${option.period}`}
                    sx={{
                      minHeight: 52,
                      gap: 2,
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                    >
                      {option.metric}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      {option.period}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            )}
            {!isMobile && (
              <>
                <Select
                  value={metric}
                  onChange={(event) =>
                    setMetric(event.target.value as EstimateMetric)
                  }
                  size="small"
                  inputProps={{ "aria-label": "Estimate metric" }}
                  sx={{ ...controlSx, minWidth: 0 }}
                >
                  <MenuItem value="revenue">Revenue</MenuItem>
                  <MenuItem value="eps">EPS</MenuItem>
                </Select>
                <Select
                  value={period}
                  onChange={(event) =>
                    changePeriod(event.target.value as EstimatePeriod)
                  }
                  size="small"
                  inputProps={{ "aria-label": "Estimate period" }}
                  sx={{ ...controlSx, minWidth: 0 }}
                >
                  <MenuItem value="annual">Annual</MenuItem>
                  <MenuItem value="quarter">Quarterly</MenuItem>
                </Select>
              </>
            )}
            <Box
              role="group"
              aria-label="Forecast horizon"
              onKeyDown={(event) => {
                if (event.key === "ArrowLeft") {
                  event.preventDefault();
                  stepHorizon(-1);
                }
                if (event.key === "ArrowRight") {
                  event.preventDefault();
                  stepHorizon(1);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                width: { xs: 160, sm: 128 },
                height: { xs: 44, sm: 34 },
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.default",
                "& .MuiIconButton-root": {
                  width: { xs: 44, sm: 32 },
                  height: { xs: 44, sm: 32 },
                  borderRadius: 0.75,
                  color: "text.secondary",
                  "&:hover": { color: "primary.main", bgcolor: "action.hover" },
                  "&.Mui-disabled": { color: "text.disabled" },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: -2,
                  },
                },
              }}
            >
              <IconButton
                aria-label="Shorter forecast horizon"
                disabled={!canShowData || horizonIndex <= 0}
                onClick={() => stepHorizon(-1)}
              >
                <ChevronLeft size={17} />
              </IconButton>
              <Typography
                component="span"
                aria-label={
                  horizon
                    ? `${horizon} ${period === "annual" ? "year" : "quarter"}${horizon === 1 ? "" : "s"}`
                    : "Horizon"
                }
                aria-live="polite"
                aria-atomic="true"
                sx={{
                  flex: 1,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  fontVariantNumeric: "tabular-nums",
                  color: canShowData ? "text.primary" : "text.disabled",
                }}
              >
                {horizonLabel}
              </Typography>
              <IconButton
                aria-label="Longer forecast horizon"
                disabled={
                  !canShowData || horizonIndex >= availableHorizons.length - 1
                }
                onClick={() => stepHorizon(1)}
              >
                <ChevronRight size={17} />
              </IconButton>
            </Box>
          </Box>
          <Tooltip title={viewAction}>
            <IconButton
              aria-label={viewAction}
              onClick={toggleView}
              sx={{
                width: { xs: 44, sm: 34 },
                height: { xs: 44, sm: 34 },
                gridArea: "view",
                justifySelf: "end",
                border: { xs: 0, sm: 1 },
                borderColor: "divider",
                borderRadius: 1,
                color: "text.secondary",
                bgcolor: { xs: "transparent", sm: "background.default" },
                "&:hover": { color: "primary.main" },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              {view === "chart" && <Table2 size={17} />}
              {view === "table" && <ChartLine size={17} />}
            </IconButton>
          </Tooltip>
        </Box>
        {isLoading && (
          <Skeleton
            aria-label="Loading analyst estimates"
            variant="rounded"
            height={360}
          />
        )}
        {hasError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  if (queryFailed) void query.refetch();
                  if (incomeFailed) void income.refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Unable to load analyst estimates.
          </Alert>
        )}
        {showEmpty && (
          <Box sx={{ minHeight: 220, display: "grid", placeItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No {periodLabel.toLowerCase()} {metricLabel} estimates available.
            </Typography>
          </Box>
        )}
        {canShowData && view === "chart" && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "space-between", sm: "flex-end" },
                gap: { xs: 1, sm: 1.5 },
                mb: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: { xs: "none", sm: "block" },
                  color: "text.secondary",
                  mr: "auto",
                }}
              >
                {metricLabel}
              </Typography>
              {[
                { key: "actual" as const, label: "Actual" },
                ...ESTIMATE_LEVELS,
              ].map(({ key, label }) => (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 0.5, sm: 0.75 },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 12, sm: 18 },
                      borderTop: `2px ${key === "actual" ? "solid" : "dotted"} ${colors[key]}`,
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.6875rem", sm: "0.75rem" } }}
                  >
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <AnalystEstimatesChart
              key={`${ticker}-${period}-${metric}-${horizon}`}
              ranges={ranges}
              actuals={actuals}
              metricLabel={metricLabel}
              colors={colors}
              formatValue={formatValue}
            />
          </>
        )}
        {canShowData && view === "table" && (
          <TableContainer>
            <Table
              size="small"
              aria-label={`${periodLabel} ${metricLabel} analyst estimates`}
              sx={{
                minWidth: 480,
                fontVariantNumeric: "tabular-nums",
                "& th, & td": {
                  px: 1.5,
                  py: 1.5,
                  borderColor: alpha(theme.palette.text.primary, 0.07),
                  whiteSpace: "nowrap",
                },
                "& thead th": {
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  color: "text.secondary",
                  bgcolor: "action.hover",
                },
                "& tbody td": { fontSize: "0.8125rem" },
                "& tbody tr:last-child td, & tbody tr:last-child th": {
                  borderBottom: 0,
                },
                "& tbody tr:hover": { bgcolor: "action.hover" },
                "& .estimate-average": {
                  bgcolor: alpha(chartColors.positive, 0.06),
                  fontWeight: 600,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>{periodColumn}</TableCell>
                  {ESTIMATE_LEVELS.map(({ key, label }) => (
                    <TableCell
                      key={key}
                      align="right"
                      className={`estimate-${key}`}
                    >
                      {label}
                    </TableCell>
                  ))}
                  <TableCell align="right">Analysts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranges.map((range) => (
                  <TableRow key={range.date}>
                    <TableCell component="th" scope="row">
                      <Typography
                        sx={{ fontSize: "0.8125rem", fontWeight: 500 }}
                      >
                        {range.period}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6875rem" }}
                      >
                        {dayjs(range.date).format("MMM D, YYYY")}
                      </Typography>
                    </TableCell>
                    {ESTIMATE_LEVELS.map(({ key }) => (
                      <TableCell
                        key={key}
                        align="right"
                        className={`estimate-${key}`}
                      >
                        {formatValue(range[key])}
                      </TableCell>
                    ))}
                    <TableCell align="right" sx={{ color: "text.secondary" }}>
                      {range.analysts ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ChartCardWrapper>
    </Box>
  );
};
